import { DatePipe } from "@angular/common";
import {
    Component,
    OnChanges,
    OnDestroy,
    OnInit,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { BusySpinnerComponent } from "@components/busy/busy-spinner/busy-spinner.component";
import { ButtonComponent } from "@components/button/button.component";
import { CheckboxComponent } from "@components/checkbox/checkbox.component";
import { DatepickerComponent } from "@components/datepicker/datepicker.component";
import { RadiobuttonGroupComponent } from "@components/radiobuttongroup/radiobuttongroup.component";
import { SpanComponent } from "@components/span/span.component";
import { TextmultilineComponent } from "@components/textmultiline/textmultiline.component";
import { NgbPopover } from "@ng-bootstrap/ng-bootstrap";
import _, { isNil } from "lodash";
import moment from "moment";
import {
    Observable,
    Subject,
    asapScheduler,
    combineLatest,
    merge,
    of
} from "rxjs";
import {
    debounceTime,
    filter,
    map,
    takeUntil,
    withLatestFrom
} from "rxjs/operators";
import { Subtyp1, Subtyp2 } from "~app/common/components/map/symbols/kartgeometri.constants";
import { ModalSettings } from "~app/common/components/modal/modal-models";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { AvverkningsanmalanSkogstyper, ConfirmDialogResponse } from "~app/common/models/constants";
import { RuleErrorContext, RuleSeverityLevel, RuleValidationMessage, RuleVisibilityStrategy } from "~app/common/services/cqs/models/rule-validation-message";
import { DateUtils } from "~app/common/utils/date-utils";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { AtgardService } from "~app/features/virkeskop/services/virkeskop-atgard.service";
import { canEdit } from "~app/role-permissions";
import { environment } from "~environments/environment";
import { Atgard } from "../../models/atgard-models";
import { Avverkningsanmalan, AvverkningsanmalanRaskStatus, IAtgardNettoareal, VKAvverkningsanmalanDTO } from "../../models/avverkningsanmalan-models";
import { Geometri } from "../../models/karta-models";
import { Planeringsstatus } from "../../models/planeringsstatus";
import { Atgardstyper } from "../../models/virkeskopAtgardstyper";
import { AtgardMediatorService } from "../../services/atgard-mediator.service";
import { AvtalMediatorService } from "../../services/avtal-mediator.service";
import { AvverkningsanmalanMediatorService } from "../../services/avverkningsanmalan-mediator.service";
import { KontraktMediatorService } from "../../services/kontrakt-mediator.service";
import { VirkeskopValidationService } from "../../services/validation/virkeskop-validation.service";
import { VkKartaMediatorService } from "../../services/vk-karta-mediator.service";
import { VirkeskopValidationMessageComponent } from "../../validation/validation-message/virkeskop-validation-message.component";
import { AvverkningsanmalanStatus } from "./avverkningsanmalan-status";
import { AvverkningsanmalanTyp } from "./avverkningsanmalan-typ";
@Component({
    selector: "rask-atgard-avverkningsanmalan",
    templateUrl: "./atgard-avverkningsanmalan.component.html",
    styleUrls: ["./atgard-avverkningsanmalan.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        VirkeskopValidationMessageComponent,
        RadiobuttonGroupComponent,
        FormsModule,
        ButtonComponent,
        NgbPopover,
        TextmultilineComponent,
        CheckboxComponent,
        DatepickerComponent,
        SpanComponent,
        BusySpinnerComponent,
        DatePipe
    ]
})
export class AtgardAvverkningsanmalanComponent implements OnChanges, OnInit, OnDestroy {
    anmalanStatusMessage = "";
    atgardGeometri: Geometri;
    avslagDatum: Date;
    avverkningsanmalanTyp = AvverkningsanmalanTyp;
    canSend: boolean;
    kontraktGeometrier: Geometri[];
    maxGodkantDatum: Date;
    radioButtonsAnmalanSkogstyp = [
        {
            key: AvverkningsanmalanSkogstyper.normalskog,
            value: AvverkningsanmalanSkogstyper.normalskog
        },
        {
            key: AvverkningsanmalanSkogstyper.adellov,
            value: "Ädellövskog"
        }
        // { key: AvverkningsanmalanSkogstyper.fjallnara, value: AvverkningsanmalanSkogstyper.fjallnara }
    ];
    radioButtonsAnsvarTyp = [];

    protected kontraktReadonly = false;

    private readonly addDaysFarStartaDatum = 42;
    private readonly addYearsGiltigTillDatum = 5;
    private readonly atgardMediatorService = inject(AtgardMediatorService);
    private readonly atgardService = inject(AtgardService);
    private readonly avtalMediatorService = inject(AvtalMediatorService);
    private readonly avverkningsanmalanMediatorService = inject(AvverkningsanmalanMediatorService);
    private readonly customAvverkningsanmalanTypSveaskogOrAnmalanWebb = "sveaskogOrAnmalanWebb";
    private readonly dialogBottomText = "<br>Innan kompletteringen är utförd kan du endast öppna Anmälan Webb, men inte skapa din anmalan/ansökan.";
    private readonly dialogTopText = "För att du ska kunna registrera din anmälan/ansökan i Anmälan Webb behöver du komplettera med följande information:";
    private readonly godkandDatumChangeSrc = new Subject<void>();
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly modalService = inject(RaskModalService);
    private readonly ngUnsubscribe$ = new Subject<void>();
    private readonly validationService = inject(VirkeskopValidationService);
    private readonly vkKartaMediatorService = inject(VkKartaMediatorService);

    private kontraktNettoareal = 0;
    private openavverkningsanmalanWebbMessageText: string;
    private peristedGeometryWkt: string;

    private readonly canEdit = canEdit();

    constructor() {
        this.radioButtonsAnsvarTyp = [
            {
                key: this.customAvverkningsanmalanTypSveaskogOrAnmalanWebb,
                value: "Sveaskog"
            },
            {
                key: AvverkningsanmalanTyp.leverantor,
                value: "Leverantör"
            }
        ];
    }

    get activeAnmalanSkogstyp(): string {
        return this.atgard.anmalanSkogstyp;
    }

    get atgard(): Atgard {
        return this.atgardMediatorService.atgard;
    }

    get avverkningsanmalan(): Avverkningsanmalan {
        return this.avverkningsanmalanMediatorService.avverkningsanmalan;
    }

    ngOnChanges(): void {
        this.maxGodkantDatum = moment().set({
            hour: 23,
            minute: 59,
            second: 59
        }).toDate();
        if (this.atgard && this.avverkningsanmalan) {
            this.setAnmalanStatusMessage();
        }
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    ngOnInit(): void {
        this.avverkningsanmalanMediatorService.init();

        this.avverkningsanmalanMediatorService.avverkningsanmalan$.pipe(
            filter(avverkningsanmalan => !isNil(avverkningsanmalan)),
            takeUntil(this.ngUnsubscribe$)
        ).subscribe(() => {
            this.validate(true);
        });

        this.avslagDatum = this.avverkningsanmalan?.avslagDatum as Date;
        this.avtalMediatorService.dirty$.pipe(
            takeUntil(this.ngUnsubscribe$)
        ).subscribe(dirty => this.canSend = !(dirty ?? false));
        this.initGeometrier();

        const validator = () => this.validate(false);

        this.avtalMediatorService.registerOnSaveCallback(validator);

        this.kontraktMediatorService.getKontraktNettoareal(this.getNettoarealCondition()).pipe(
            takeUntil(this.ngUnsubscribe$)
        ).subscribe(nettoareal => this.kontraktNettoareal = nettoareal);

        this.kontraktMediatorService.isReadonly$.pipe(
            map(readonly => readonly || !this.canEdit()),
            takeUntil(this.ngUnsubscribe$)
        ).subscribe(readonly => this.kontraktReadonly = readonly);

        this.avverkningsanmalanMediatorService.avverkningsanmalan$.pipe(
            filter(avverkningsanmalan => !isNil(avverkningsanmalan)),
            withLatestFrom(this.atgardMediatorService.atgard$),
            takeUntil(this.ngUnsubscribe$)
        ).subscribe(([
            avverkningsanmalan,
            atgard
        ]) => {
            this.kontraktMediatorService.updateNettoarealSrc.next({
                atgardGuid: atgard.guid,
                nettoareal: atgard.nettoareal,
                atgardstyp: atgard.atgardstyp,
                avverkningsanmalanGodkantDatum: avverkningsanmalan.godkantDatum,
                beteckning: atgard.beteckning
            });
        });
    }

    protected avslag(): void {
        if (this.kontraktReadonly) {
            return;
        }
        this.avverkningsanmalan.avslagDatum = this.avslagDatum ?? new Date();
        this.clearFields();
        this.update();
    }

    protected avverkningsanmalanDispensChange(): void {
        if (!this.avverkningsanmalan.dispens) {
            this.setAvverkningFarStartaDatumForAnmalan();
        } else {
            this.update();
        }
    }

    protected avverkningsanmalanFarStartaDatumChange(farStartaDatum: string): void {
        this.update();

        if (this.avverkningsanmalan.ansokan) {
            this.setAnsokanGiltigtTillDatum();
        }
    }

    protected avverkningsanmalanGodkantDatumChange(): void {
        this.godkandDatumChangeSrc.next();
        this.avverkningsanmalan.avslagDatum = null;
        if (this.avverkningsanmalan.ansokan) {
            if (this.avverkningsanmalan.godkantDatum) {
                this.setAnsokanGiltigtTillDatum();
            }
        } else {
            this.setAvverkningFarStartaDatumForAnmalan();
        }
    }

    protected changeAnmalanSkogsTyp(typ: string): void {
        this.atgard.anmalanSkogstyp = typ;

        this.atgardMediatorService.update(this.atgard);

        asapScheduler.schedule(() => {
            this.update();
        });
    }

    protected changeAvverkningsanmalanAnsvarigTyp(typ: string): void {

        if (typ === AvverkningsanmalanTyp.leverantor) {
            this.avverkningsanmalan.typ = AvverkningsanmalanTyp.leverantor;
            // Kollar ifall skogstyp är satt, töm i så fall fältet för leverantör.
            if (this.atgard.anmalanSkogstyp) {
                this.atgard.anmalanSkogstyp = null;
                this.atgardMediatorService.update(this.atgard);
            }
        } else {
            this.avverkningsanmalan.typ = AvverkningsanmalanTyp.anmalanWeb;
        }

        this.clearFields();
        asapScheduler.schedule(() => {
            this.update();
        });
    }

    protected changeAvverkningsanmalanTyp(): void {
        if (this.avverkningsanmalan.ansokan) {
            this.setAnsokanGiltigtTillDatum();
        } else {
            this.setAvverkningFarStartaDatumForAnmalan();
        }
        this.update();
    }

    protected getAnmalanSkogsTyp(): string {
        return this.atgard.anmalanSkogstyp;
    }

    protected getAvverkningsanmalanAnsvarigTyp(): string {
        if (this.avverkningsanmalan.typ && this.avverkningsanmalan.typ === AvverkningsanmalanTyp.leverantor) {
            return AvverkningsanmalanTyp.leverantor;
        }
        return this.customAvverkningsanmalanTypSveaskogOrAnmalanWebb;

    }

    protected getAvverkningsanmalanAvverkningFarStartaDatum(): string {
        if (this.avverkningsanmalan.avverkningFarStartaDatum) {
            return moment(this.avverkningsanmalan.avverkningFarStartaDatum).format(DateUtils.dateformat);
        } else {
            return null;
        }
    }

    protected getAvverkningsanmalanGodkantDatum(): string {
        if (this.avverkningsanmalan.godkantDatum) {
            return moment(this.avverkningsanmalan.godkantDatum).format(DateUtils.dateformat);
        } else {
            return null;
        }
    }

    protected getMaxDateFarStarta(): Date {
        if (this.avverkningsanmalan.giltigtTillDatum) {
            return this.avverkningsanmalan.giltigtTillDatum as Date;
        }
        return new Date();
    }

    protected getMinDateFarStarta(): Date {
        if (this.avverkningsanmalan.godkantDatum) {
            return moment(this.avverkningsanmalan.godkantDatum)
                .add(1, "days")
                .toDate();
        }
        return new Date();
    }

    protected getNettoarealCondition() {
        const foryngringsavverkningOrVagLednAtgardstyp = [
            ...Atgardstyper.foryngringsavverkningstyper,
            Atgardstyper.VagLedning
        ];
        return (areal: IAtgardNettoareal) => foryngringsavverkningOrVagLednAtgardstyp.some(typ => typ === areal.atgardstyp) &&
            !areal.avverkningsanmalanGodkantDatum;
    }

    protected getRaskStatusForAvverkningsanmalan() {
        const status = this.avverkningsanmalanMediatorService.getRaskStatusForAvverkningsanmalan(
            this.avverkningsanmalan.atgardsgeometri,
            this.peristedGeometryWkt,
            this.atgardGeometri?.mapGeometry?.wkt,
            this.avverkningsanmalan);

        if (status >= this.avverkningsanmalan.raskStatus) {
            return status;
        }

        if (status === AvverkningsanmalanRaskStatus.okand && this.avverkningsanmalan.raskStatus === AvverkningsanmalanRaskStatus.ny) {
            return AvverkningsanmalanRaskStatus.okand;
        }

        return this.avverkningsanmalan.raskStatus;
    }

    protected onBeforeChangeAvverkningsanmalanAnsvarigTyp = ($event: string): Observable<boolean> => {
        if (!this.changeAvverkningsanmalanAnsvarigTypIsDisabled() || $event !== AvverkningsanmalanTyp.leverantor) {
            return of(true);
        }

        this.modalService.info(new ModalSettings("Observera",
            "Byte till leverantör som ansvarig kan inte ske förrän anmälan återkallats, eller avslag registrerats i Anmälan Webb."));
        return of(false);
    };

    protected openAvverkningsanmalanWebb() {
        if (this.showDialogbeforeOpenAvverkningsanmalanWebb()) {
            const settings = new ModalSettings((this.avverkningsanmalan.atgardKanAvverkningsanmalas || this.atgard.status === Planeringsstatus.Tillgangligt) ?
                "Information" : "Information saknas för att kunna registrera ditt ärende", this.openavverkningsanmalanWebbMessageText);
            settings.yesCaption = "Öppna avverkningsanmälan";
            settings.noCaption = "Avbryt";
            settings.contentIsMarkup = true;
            this.modalService.confirm(settings)
                .subscribe({
                    next: openanmalan => {
                        if (openanmalan === ConfirmDialogResponse.save) {
                            window.open(this.anmalanWebbLink(), "_blank");
                        }
                    },
                    error: () => {
                        //
                    }
                });
        } else {
            window.open(this.anmalanWebbLink(), "_blank");
        }
    }

    protected skogstypCanChange() {
        return !this.changeAvverkningsanmalanAnsvarigTypIsDisabled();
    }

    protected update(): void {
        if (this.avverkningsanmalan.ansokan) {
            this.avverkningsanmalan.dispens = false;
        }
        const avverkningsanmalanDto: VKAvverkningsanmalanDTO = _.clone(this.avverkningsanmalan);
        this.avverkningsanmalanMediatorService.updateCommand(this.atgard.guid, avverkningsanmalanDto);
    }

    protected validate(isInit = false): void {
        this.validationService.clearMessagesByEntity(
            "avverkningsanmalan",
            [
                this.atgard.guid,
                this.kontraktMediatorService.kontrakt.guid
            ]);

        const messages: RuleValidationMessage[] = [];

        if (this.atgard.isReadonly) {
            return;
        }

        if (!this.avverkningsanmalan) {
            return;
        }

        const kontraktGuid = this.kontraktMediatorService.kontrakt.guid;

        if (!isInit) {
            if (this.kontraktNettoareal < 0.5 &&
                this.atgardService.isForyngringsavverkningOrVagLednAtgardstyp(this.atgard.atgardstyp) &&
                !this.avverkningsanmalan.godkantDatum) {
                messages.push({
                    entityGuids: [
                        this.atgard.guid,
                        kontraktGuid
                    ],
                    entityType: "avverkningsanmalan",
                    message: "Avverkningsanmälan är inte obligatorisk då den sammanlagda ytan understiger 0,5 ha",
                    messageId: "avverkningsanmalanEjObligatorisk",
                    severity: RuleSeverityLevel.warning,
                    strategy: RuleVisibilityStrategy.alwaysShow,
                    context: RuleErrorContext.save
                });
            }

            if (this.avverkningsanmalan.delytorAvstandOgiltig && this.atgardService.requiresAvverkningsanmalanAndMaxDistance(this.atgard.atgardstyp)) {
                messages.push({
                    entityGuids: [
                        this.atgard.guid,
                        kontraktGuid
                    ],
                    entityType: "avverkningsanmalan",
                    message: "Åtgärdens delytor ligger mer än 100 meter ifrån varandra och kräver separata avverkningsanmälningar. Korrigera ytorna, alt. skapa två åtgärder",
                    messageId: "delytorForStortAvstand",
                    severity: RuleSeverityLevel.warning,
                    strategy: RuleVisibilityStrategy.alwaysShow,
                    context: RuleErrorContext.save
                });
            }
        } else if (this.avverkningsanmalan.validationMessages?.length > 0) {
            messages.push(...this.avverkningsanmalan.validationMessages);
        }

        const status = isInit ? this.avverkningsanmalan.raskStatus : this.getRaskStatusForAvverkningsanmalan();

        if (status && status !== AvverkningsanmalanRaskStatus.okand) {
            const message = new RuleValidationMessage();
            message.strategy = RuleVisibilityStrategy.confirm;
            message.entityGuids = [
                this.atgard.guid,
                kontraktGuid
            ];
            message.entityType = "avverkningsanmalan";
            message.severity = RuleSeverityLevel.warning;

            switch (status) {
                case AvverkningsanmalanRaskStatus.ogiltig:
                    message.messageId = "avverkningsanmalanOgiltig";
                    message.message = "Åtgärdens yta har ökat med mer än 0,5 ha. Du måste göra en ny avverkningsanmälan";
                    break;
                case AvverkningsanmalanRaskStatus.andrad:
                    message.messageId = "avverkningsanmalanAndrad";

                    message.message = "Åtgärdens yta har ökat. Du behöver göra en komplettering av din avverkningsanmälan för att återfå datum för när avverkning får starta.";
                    break;
                case AvverkningsanmalanRaskStatus.ny:
                    message.messageId = "avverkningsanmalanNy";
                    message.message = "Åtgärdens yta har ökat till mer än 0,5 ha. Du måste göra en avverkningsanmälan";
                    break;
            }

            messages.push(message);
        }

        messages.forEach(message => this.validationService.updateClientSideValidation(true, message));
    }

    private addMessageTextToList(text: string): void {
        this.openavverkningsanmalanWebbMessageText += `<li>${text}</li>`;
    }

    private readonly anmalanWebbLink = (): string => `${environment.avverkningsanmalanWebbUrl}/beteckning/${this.atgard.beteckning}`;

    private changeAvverkningsanmalanAnsvarigTypIsDisabled(): boolean {
        const disabledStates = [
            AvverkningsanmalanStatus.skickad,
            AvverkningsanmalanStatus.registrerad,
            AvverkningsanmalanStatus.kompletteringKravs,
            AvverkningsanmalanStatus.kompletteringNy,
            AvverkningsanmalanStatus.kompletteringSkickad
        ];

        return disabledStates.includes(this.avverkningsanmalan.status);
    }

    private clearFields(): void {
        this.avverkningsanmalan.godkantDatum = null;
        this.avverkningsanmalan.avverkningFarStartaDatum = null;
        this.avverkningsanmalan.giltigtTillDatum = null;
        this.avverkningsanmalan.dispens = false;
    }

    private initGeometrier(): void {
        this.vkKartaMediatorService.atgardGeometrier$.pipe(
            filter(geometrier => !ObjectUtils.isNullOrUndefined(geometrier)),
            map(geometrier => geometrier.find(x => x.atgardGuid === this.atgard.guid)),
            filter(geometri => ObjectUtils.hasValue(geometri) && ObjectUtils.hasValue(geometri.kartgeometri)),
            takeUntil(this.ngUnsubscribe$)
        ).subscribe(geometri => {
            this.atgardGeometri = geometri;
            this.peristedGeometryWkt = geometri.kartgeometri.geometri;
        });

        const atgardGeometriChange$ = merge(this.vkKartaMediatorService.atgardGeometriChanged$, this.vkKartaMediatorService.atgardGeometriDeleted$).pipe(
            filter(geometri => !isNil(geometri)),
            filter(geometri => geometri.atgardGuid === this.atgard.guid)
        );

        merge(atgardGeometriChange$, this.godkandDatumChangeSrc).pipe(
            debounceTime(100),
            map(() => this.getRaskStatusForAvverkningsanmalan()),
            takeUntil(this.ngUnsubscribe$)
        ).subscribe(raskStatus => this.avverkningsanmalanMediatorService.checkSetAvverkningsanmalanRaskStatusCommand(raskStatus, this.avverkningsanmalan, this.atgard.guid));

        // Drivningspunkter, avlägg, hänsyn, mm. bundet till kontrakt
        combineLatest([
            this.vkKartaMediatorService.kontraktGeometrier$,
            this.kontraktMediatorService.kontrakt$
        ]).pipe(
            filter(([geometrier]) => !ObjectUtils.isNullOrUndefined(geometrier)),
            takeUntil(this.ngUnsubscribe$)
        ).subscribe(([
            geometrier,
            kontrakt
        ]) => {
            this.kontraktGeometrier = geometrier.filter(x => x.kontraktGuid === kontrakt.guid);
        });
    }

    private setAnmalanGiltigtTillDatumForAnmalan(): void {
        if (this.avverkningsanmalan.godkantDatum) {
            this.avverkningsanmalan.giltigtTillDatum = moment(this.avverkningsanmalan.godkantDatum)
                .add(this.addYearsGiltigTillDatum, "years")
                .toDate();
        }
        this.update();
    }

    private setAnmalanStatusMessage(): void {
        if (this.avverkningsanmalan.status === AvverkningsanmalanStatus.skickad) {
            this.anmalanStatusMessage = "Anmälan skickad till Skogsstyrelsen";
        } else if (this.avverkningsanmalan.status === AvverkningsanmalanStatus.kompletteringKravs) {
            this.anmalanStatusMessage = "Inskickad anmälan behöver kompletteras";
        } else {
            this.anmalanStatusMessage = "";
        }
    }

    private setAnsokanGiltigtTillDatum(): void {
        if (this.avverkningsanmalan.avverkningFarStartaDatum) {
            this.avverkningsanmalan.giltigtTillDatum = moment(this.avverkningsanmalan.avverkningFarStartaDatum)
                .add(this.addYearsGiltigTillDatum, "years")
                .toDate();
        } else {
            this.avverkningsanmalan.giltigtTillDatum = null;
        }
        this.update();
    }

    private setAvverkningFarStartaDatumForAnmalan(): void {
        if (this.avverkningsanmalan.godkantDatum) {
            const godkantDatum = new Date(this.avverkningsanmalan.godkantDatum.toString());
            this.avverkningsanmalan.avverkningFarStartaDatum = moment(godkantDatum)
                .add(this.addDaysFarStartaDatum, "days")
                .toDate();

            this.setAnmalanGiltigtTillDatumForAnmalan();
        }
        this.update();
    }

    private setMessagetextForkanEjAvverkningsanmalas(): void {
        if (this.atgardService.isForyngringsavverkning(this.atgard.atgardstyp)
            && this.avverkningsanmalan?.anmalanTradslag?.length > 0 !== true) {
            this.addMessageTextToList("Åtgärden måste ha en föryngringsåtgärd för att avverkningsanmälan ska kunna göras.");
        }

        if (!this.kontraktGeometrier.some(x => x.kartgeometri.subtyp2 === Subtyp2.avlagg)) {
            this.addMessageTextToList("Åtgärden (Kontraktet) måste ha minst ett avlägg för att avverkningsanmälan ska kunna göras.");
        }

        if (this.atgard.geometri === null) {
            this.addMessageTextToList("Åtgärden måste ha en geometri för att avverkningsanmälan ska kunna göras.");
        }

        if (this.atgardService.requiresAvverkningsanmalanAndMaxDistance(this.atgard.atgardstyp) && this.avverkningsanmalan.delytorAvstandOgiltig) {
            this.addMessageTextToList("Åtgärdens delytor ligger mer än 100 meter ifrån varandra och kräver separata avverkningsanmälningar.");
        }
    }

    private showDialogbeforeOpenAvverkningsanmalanWebb(): boolean {
        let openDialog = false;

        if (this.atgard.status === Planeringsstatus.Tillgangligt) {
            this.openavverkningsanmalanWebbMessageText = "<ul><li>Åtgärdens status är Tillgängligt och kan inte anmälas. Om den finns i Anmälan Webb kan den ändå visas.</li></ul>";
            return true;
        }

        this.openavverkningsanmalanWebbMessageText = !this.avverkningsanmalan.atgardKanAvverkningsanmalas ? this.dialogTopText : "";
        this.openavverkningsanmalanWebbMessageText += "<ul>";
        if (!this.avverkningsanmalan.atgardKanAvverkningsanmalas) {
            this.setMessagetextForkanEjAvverkningsanmalas();
            openDialog = true;
        }

        if (this.atgardService.isMarkberedningForyngringRecommended(this.atgard.atgardstyp)
            && this.avverkningsanmalan?.anmalanTradslag?.length > 0 !== true) {
            this.addMessageTextToList("Åtgärden saknar föryngringsåtgärd. Finns föryngringsåtgärder ska de anges innan anmälan görs.");
            openDialog = true;
        }

        if (!this.kontraktGeometrier.some(x => x.kartgeometri.subtyp1 === Subtyp1.hansynLinje
            || x.kartgeometri.subtyp1 === Subtyp1.hansynYta || x.kartgeometri.subtyp1 === Subtyp1.hansynPunkt)) {
            this.addMessageTextToList("Åtgärden (Kontraktet) saknar inritade hänsyn. Finns hänsyn ska de ritas in innan anmälan görs.");
            openDialog = true;
        }

        if (!this.atgard.anmalanSkogstyp && this.skogstypCanChange()) {
            this.addMessageTextToList("Du måste ange skogstyp för avverkningsanmälan.");
            openDialog = true;
        }

        this.openavverkningsanmalanWebbMessageText += "</ul>";
        this.openavverkningsanmalanWebbMessageText += !this.avverkningsanmalan.atgardKanAvverkningsanmalas ? this.dialogBottomText : "";

        return openDialog;
    }
}
